import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthSessionService } from './auth-session.service';
import { ConfigService, QRDAEnvironment } from './config.service';

type LogRocketAPI = {
  init(appId: string): void;
  identify(id: string, props?: Record<string, any>): void;
  sessionURL?: string;
};

@Injectable({ providedIn: 'root' })
export class LogRocketService {
  private logRocket?: LogRocketAPI;
  public initialized$ = new BehaviorSubject<boolean>(false);
  private pendingUser: any = null; // Store user if identify is called too early
  private readonly STORAGE_KEY = 'logrocket-enabled';
  // add 'local' to the allowed array for local test
  private readonly ALLOWED_ENVS: QRDAEnvironment[] = ['local', 'dev', 'test'/* , 'prod' */];

  private enabled$ = new BehaviorSubject<boolean>(
    localStorage.getItem(this.STORAGE_KEY) === 'true'
  );

  constructor(private config: ConfigService) {
    console.log(`[LogRocketService] Constructor. Initial storage state: ${this.enabled$.value}`);
    if (this.enabled$.value && !AuthSessionService.isGuestSignLinkBoot()) {
      console.log('[LogRocketService] Auto-initializing based on saved preference...');
      queueMicrotask(() => this.init());
    }
  }

  public get envAllowed(): boolean {
    return this.ALLOWED_ENVS.includes(this.config.environment);
  }

  async enable(): Promise<void> {
    console.log('[LogRocketService] Action: Enable called');
    if (AuthSessionService.isGuestSignLinkBoot()) {
      console.warn('[LogRocketService] Enable skipped: guest sign-link boot.');
      return;
    }
    if (!this.envAllowed) {
      console.warn('[LogRocketService] Enable aborted: Environment not allowed.');
      return;
    }
    localStorage.setItem(this.STORAGE_KEY, 'true');
    this.enabled$.next(true);
    console.log('[LogRocketService] LocalStorage set to true. Starting init...');
    await this.init();
  }

  disable(): void {
    console.log('[LogRocketService] Action: Disable called');
    localStorage.setItem(this.STORAGE_KEY, 'false');
    this.enabled$.next(false);
    this.initialized$.next(false);
    console.log('[LogRocketService] LocalStorage set to false. Reloading page in 100ms...');

    setTimeout(() => {
      location.reload();
    }, 100);
  }
  private async init(): Promise<void> {
    if (typeof window === 'undefined' || this.initialized$.value) return;
    if (AuthSessionService.isGuestSignLinkBoot()) {
      return;
    }

    try {
      const logRocketModule = await import('logrocket');
      const LogRocket = (logRocketModule as any).default ?? logRocketModule;

      LogRocket.init(environment.logRocket.appId);
      this.logRocket = LogRocket as LogRocketAPI;

      // ✅ Check if identify() was called while we were still importing
      if (this.pendingUser) {
        console.log('[LogRocketService] Applying pending identification');
        this.identify(this.pendingUser);
        this.pendingUser = null;
      } else {
        this.identify(); // Default to anonymous if nothing pending
      }

      this.initialized$.next(true);
      console.log('[LogRocketService] ✅ Full Initialization Complete.');
    } catch (err) {
      console.error('[LogRocketService] ❌ Init failed', err);
    }
  }

  identify(user?: { id: string; name?: string; email?: string;[key: string]: any; }): void {
    if (AuthSessionService.isGuestSignLinkBoot()) {
      return;
    }
    // If the module isn't ready yet, save the user for later
    if (!this.logRocket) {
      console.log('[LogRocketService] Identify called before init. Saving user data...');
      this.pendingUser = user || 'anonymous';
      return;
    }

    const userId = user?.id || 'anonymous';
    console.log(`[LogRocketService] 👤 Identifying user in LogRocket: ${userId}`);

    if (user && typeof user !== 'string') {
      this.logRocket.identify(user.id, { name: user.name, email: user.email, ...user });
    } else {
      this.logRocket.identify('anonymous');
    }
  }

  get enabled(): boolean {
    return this.enabled$.value;
  }
}